/**
 * W.E.E. Guided Access workspace model.
 * Internal workspace shells are intentionally limited to:
 *   1. Admin
 *   2. Staff / Volunteer
 *
 * Role-based permissions still control which routes and records a person
 * can actually access. Sharing the Staff / Volunteer shell does not grant
 * volunteers staff permissions.
 */

export type WorkspaceId = 'admin' | 'staff'

export interface WorkspaceNavItem {
  label: string
  path: string
  icon: string
  badge?: 'pendingHours' | 'inquiries' | 'notifications'
  divider?: boolean
}

export interface WorkspaceConfig {
  id: WorkspaceId
  label: string
  emoji: string
  description: string
  homeRoute: string
  navItems: WorkspaceNavItem[]
  allowedPaths: string[]
}

export const WORKSPACES: Record<WorkspaceId, WorkspaceConfig> = {
  admin: {
    id: 'admin',
    label: 'Admin Workspace',
    emoji: '👑',
    description: 'Full system access — all modules, records, and settings',
    homeRoute: '/dashboard',
    allowedPaths: ['*'],
    navItems: [
      { label: 'Dashboard', path: '/dashboard', icon: 'HiHome' },
      { label: 'Work Queue', path: '/work-queue', icon: 'HiInbox', badge: 'inquiries' },
      { label: 'Client & Family Services', path: '/workspace/clients', icon: 'HiHeart' },
      { label: 'Agencies & Partnerships', path: '/workspace/partners', icon: 'HiOfficeBuilding' },
      { label: 'Volunteer Services', path: '/workspace/volunteers', icon: 'HiUsers' },
      { label: 'Operations & Administration', path: '/workspace/operations', icon: 'HiShieldCheck' },
    ],
  },

  staff: {
    id: 'staff',
    label: 'Staff Workspace',
    emoji: '👥',
    description: 'Role-appropriate operational access to W.E.E. workspaces',
    homeRoute: '/dashboard',
    allowedPaths: ['/dashboard', '/workspace/clients', '/workspace/partners', '/workspace/volunteers', '/workspace/operations', '/work-queue', '/cases', '/cases/:id', '/appointments', '/inquiries', '/stabilization', '/stabilization/walk-in', '/profile', '/volunteer-portal'],
    navItems: [
      { label: 'Dashboard', path: '/dashboard', icon: 'HiHome' },
      { label: 'Work Queue', path: '/work-queue', icon: 'HiInbox' },
      { label: 'Client & Family Services', path: '/workspace/clients', icon: 'HiHeart' },
      { label: 'Agencies & Partnerships', path: '/workspace/partners', icon: 'HiOfficeBuilding' },
      { label: 'Volunteer Services', path: '/workspace/volunteers', icon: 'HiUsers' },
      { label: 'Operations & Administration', path: '/workspace/operations', icon: 'HiShieldCheck' },
    ],
  }
}

export function getWorkspaceForRole(_role: string, isAdmin: boolean): WorkspaceConfig {
  return isAdmin ? WORKSPACES.admin : WORKSPACES.staff
}

export const ADMIN_PREVIEWABLE_WORKSPACES: WorkspaceId[] = ['admin', 'staff']
